import {
  supabase, uid, invokeFunction,
  AVATAR_IMAGE_SELECT,
  resolveStoredImageUrl, getPublicImageUrl, getStoredImageFocus,
  buildImageColumns, buildImageFocusColumns,
  getStoredImageAsset, sameStoredImageAsset, safeDeleteStoredImage,
} from './shared'
import type { RecordLike } from './shared'
import { ProfileCssValidationError, sanitizeProfileCss } from '@/lib/profileCssSanitizer'
import { PROFILE_SCHEMA_VERSION, validateAndNormalizeProfileThemeVariables } from '@/lib/profileTheme'
import type { PublicProfileResponse } from '@/types/api'
import type { AdminAccessRequest, RequestAccessRequest, UpdateMyProfileRequest } from '../../contract'

interface ProfileRowLike {
  id?: string | null
  email?: string | null
  username?: string | null
  display_name?: string | null
  bio?: string | null
  custom_css?: string | null
  custom_css_enabled?: boolean | null
  theme_preset?: string | null
  theme_variables?: unknown
  profile_schema?: string | null
}

export const PROFILE_SELF_SELECT = [
  'id', 'email', 'username', 'display_name',
  'avatar_url', 'avatar_provider', 'avatar_bucket', 'avatar_storage_path',
  'avatar_mime_type', 'avatar_bytes', 'avatar_width', 'avatar_height', 'avatar_original_name',
  'avatar_focus_x', 'avatar_focus_y',
  'bio', 'custom_css', 'custom_css_enabled', 'theme_preset', 'theme_variables',
].join(', ')

function normalizeThemeVariablesPayload(value: unknown) {
  const { themeVariables, errors } = validateAndNormalizeProfileThemeVariables(value)
  return { themeVariables, errors }
}

export function mapProfileRow(
  row: unknown,
  { includeEmail = false }: { includeEmail?: boolean } = {},
): (PublicProfileResponse & { email?: string }) | null {
  if (!row || typeof row !== 'object') return null
  const profileRow = row as ProfileRowLike
  const { themeVariables } = normalizeThemeVariablesPayload(profileRow.theme_variables)
  const profile: PublicProfileResponse & { email?: string } = {
    id: profileRow.id || '',
    username: profileRow.username || '',
    display_name: profileRow.display_name || '',
    avatar_url: resolveStoredImageUrl({
      row: profileRow,
      prefix: 'avatar',
      getPublicUrl: getPublicImageUrl,
    }) || '',
    avatar_focus: getStoredImageFocus(profileRow, 'avatar'),
    bio: profileRow.bio || '',
    custom_css: profileRow.custom_css || '',
    custom_css_enabled: Boolean(profileRow.custom_css_enabled),
    theme_preset: profileRow.theme_preset || 'default',
    theme_variables: themeVariables,
    profile_schema: profileRow.profile_schema || PROFILE_SCHEMA_VERSION,
  }
  if (includeEmail) {
    profile.email = profileRow.email || ''
  }
  return profile
}

export function buildProfileUpdatePayload(fields: UpdateMyProfileRequest = {}) {
  const payload: RecordLike = {}
  const errors: string[] = []

  if (fields.display_name !== undefined) {
    payload.display_name = fields.display_name.trim()
  }
  if (fields.avatar_url !== undefined) {
    payload.avatar_url = fields.avatar_url.trim()
  }
  if (fields.avatar_asset !== undefined) {
    Object.assign(payload, buildImageColumns('avatar', fields.avatar_asset))
  }
  if (fields.avatar_focus !== undefined) {
    Object.assign(payload, buildImageFocusColumns('avatar', fields.avatar_focus))
  }
  if (fields.avatar_asset === null) {
    Object.assign(payload, buildImageFocusColumns('avatar', null))
  }
  if (fields.bio !== undefined) {
    payload.bio = fields.bio
  }
  if (fields.custom_css !== undefined) {
    const sanitized = sanitizeProfileCss(fields.custom_css)
    if (sanitized.errors.length) {
      errors.push(...sanitized.errors)
    } else {
      payload.custom_css = sanitized.css
    }
  }
  if (fields.custom_css_enabled !== undefined) {
    payload.custom_css_enabled = Boolean(fields.custom_css_enabled)
  }
  if (fields.theme_preset !== undefined) {
    payload.theme_preset = fields.theme_preset.trim() || 'default'
  }
  if (fields.theme_variables !== undefined) {
    const normalizedTheme = normalizeThemeVariablesPayload(fields.theme_variables)
    if (normalizedTheme.errors.length) {
      errors.push(...normalizedTheme.errors)
    } else {
      payload.theme_variables = normalizedTheme.themeVariables
    }
  }
  if (errors.length) {
    throw new ProfileCssValidationError([...new Set(errors)])
  }
  return payload
}

export async function getMyProfile() {
  const userId = await uid()
  const { data, error } = await supabase
    .from('profiles')
    .select(PROFILE_SELF_SELECT)
    .eq('id', userId)
    .maybeSingle()

  if (error) throw error

  const row = mapProfileRow(data, { includeEmail: true })
  return row ? { ...row, email: row.email ?? '' } : null
}

export async function getPublicProfile(profileId: string) {
  if (!profileId) {
    return null
  }

  const { data, error } = await supabase.rpc('rpc_get_public_profile_v1', {
    p_profile_id: profileId,
  })

  if (error) throw error

  const row = Array.isArray(data) ? data[0] ?? null : data
  return mapProfileRow(row)
}

export async function updateMyProfile(request: UpdateMyProfileRequest = {}) {
  try {
    const userId = await uid()
    const avatarAssetProvided = Object.prototype.hasOwnProperty.call(request, 'avatar_asset')
    const previousAvatar = avatarAssetProvided
      ? getStoredImageAsset(
        (await supabase
          .from('profiles')
          .select(AVATAR_IMAGE_SELECT)
          .eq('id', userId)
          .maybeSingle()
        ).data,
        'avatar',
      )
      : null
    const payload = buildProfileUpdatePayload(request)

    if (!Object.keys(payload).length) {
      return getMyProfile()
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(payload)
      .eq('id', userId)
      .select(PROFILE_SELF_SELECT)
      .single()

    if (error) throw error

    const row = mapProfileRow(data, { includeEmail: true })
    const result = row ? { ...row, email: row.email ?? '' } : null
    if (avatarAssetProvided && previousAvatar && !sameStoredImageAsset(previousAvatar, request.avatar_asset)) {
      await safeDeleteStoredImage(previousAvatar)
    }
    return result
  } catch (error) {
    if (request?.avatar_asset) {
      await safeDeleteStoredImage(request.avatar_asset)
    }
    throw error
  }
}

export async function requestPasswordReset(email: string) {
  const redirectTo = `${window.location.origin}/reset-password`;
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });

  if (error) throw error;

  return { message: 'Password reset email sent', success: true };
}

export async function updatePassword(request: string | { newPassword?: string; currentPassword?: string }) {
  const newPassword = typeof request === 'string' ? request : request?.newPassword;
  const currentPassword = typeof request === 'string' ? undefined : request?.currentPassword;

  if (!newPassword) {
    return { message: 'New password is required.', success: false };
  }

  if (currentPassword) {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      return { message: userError.message || 'Failed to verify current session.', success: false };
    }

    const email = userData?.user?.email;
    if (!email) {
      return {
        message: 'Could not verify your current password. Try the password reset email flow instead.',
        success: false,
      };
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    });

    if (signInError) {
      return {
        message: 'Current password is incorrect.',
        success: false,
      };
    }
  }

  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) {
    return {
      message: error.message || 'Failed to update password',
      success: false,
    };
  }

  if (!data?.user) {
    return {
      message: 'Password update did not complete. Please try again.',
      success: false,
    };
  }


  return { message: 'Password updated successfully', success: true };
}

export async function createAccount(email: string, password: string) {
  const { data, error } = await supabase.auth.signUp({ email, password });

  if (error) {
    return {
      message: error.message || 'Failed to create account',
      success: false,
      requires_confirmation: false,
    };
  }

  const needsConfirmation = !data?.session;
  const message = needsConfirmation
    ? 'Account created. Check your email to confirm before logging in.'
    : 'Account created. You can now log in.';

  return { message, success: true, requires_confirmation: needsConfirmation };
}

export async function requestAccess(request: RequestAccessRequest) {
  return invokeFunction('request-access', request)
}

export async function adminAccess(request: AdminAccessRequest) {
  return invokeFunction('admin-access', request)
}
